import type { HandOffConversation } from '../../types/handoff';
import {
  useAssignHandoffMutation,
  useResolveHandoffMutation,
  useTakeHandoffMutation,
} from '../../hooks/useHandoff';
import { useAgentRosterQuery } from '../../hooks/useAgents';
import { agentRealName } from '../../types/agents';
import { useAuth } from '../../utils/authcontextt';

type Props = {
  conversation: HandOffConversation | null;
};

export function HandoffContextPanel({ conversation }: Props) {
  const takeHandoff = useTakeHandoffMutation();
  const resolveHandoff = useResolveHandoffMutation();
  const assignHandoff = useAssignHandoffMutation();
  const { data: roster = [] } = useAgentRosterQuery();
  const { user } = useAuth();

  if (!conversation) {
    return (
      <aside className="border-l bg-white p-5">
        <h2 className="text-sm font-semibold text-gray-950">Context</h2>
        <p className="mt-2 text-sm text-gray-500">Ingen chatt vald.</p>
      </aside>
    );
  }

  const isMine = Boolean(conversation.assignedAgentId && conversation.assignedAgentId === user?.id);
  const isEmailDraftCase = conversation.caseType === 'email_draft';
  const mailFailure =
    conversation.escalationEmailError ||
    (conversation.emailDraftStatus === 'failed' ? conversation.emailDraftLastError : null) ||
    (conversation.emailDraftStatus === 'failed' ? 'okänd orsak' : null);
  const canTake =
    !isEmailDraftCase &&
    (conversation.handoffStatus === 'requested' ||
      ((conversation.handoffStatus === 'assigned' || conversation.handoffStatus === 'active') &&
        !isMine));
  const canResolve =
    conversation.handoffStatus != null && conversation.handoffStatus !== 'resolved';
  const canAssign = conversation.handoffStatus !== 'resolved';

  const assignedEntry = conversation.assignedAgentId
    ? roster.find((agent) => agent.userId === conversation.assignedAgentId)
    : undefined;
  const handledBy = assignedEntry
    ? `${agentRealName(assignedEntry)} → ${assignedEntry.alias}`
    : conversation.assignedAgent;

  return (
    <aside className="min-h-0 overflow-y-auto border-l bg-white p-5">
      <div>
        <h2 className="text-sm font-semibold text-gray-950">Ärende</h2>

        <dl className="mt-4 space-y-4 text-sm">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Status</dt>
            <dd className="mt-1 text-gray-900">{conversation.handoffStatus}</dd>
          </div>

          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Kanal</dt>
            <dd className="mt-1 text-gray-900">{conversation.channel || 'webchat'}</dd>
          </div>

          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Kund</dt>
            <dd className="mt-1 text-gray-900">{conversation.customerName || 'Okänd kund'}</dd>
          </div>

          {handledBy && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">
                Hanteras av
              </dt>
              <dd className="mt-1 break-all text-gray-900">{handledBy}</dd>
            </div>
          )}

          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">
              Senaste meddelande
            </dt>
            <dd className="mt-1 rounded-xl bg-gray-50 p-3 text-gray-700">
              {conversation.lastMessage || 'Ingen sammanfattning ännu.'}
            </dd>
          </div>
        </dl>
      </div>

      <div className="mt-6 space-y-3">
        {mailFailure && (
          <div className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-800">
            <p className="font-semibold">
              Mejlet till kundtjänst gick INTE ut. Hantera ärendet manuellt.
            </p>
            <p className="mt-1 font-mono text-xs text-red-700">Orsak: {mailFailure}</p>
          </div>
        )}
        {isEmailDraftCase ? (
          <div className="rounded-xl bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-800">
            {conversation.emailDraftStatus === 'failed'
              ? 'Kundmejl via chatten – utkastet är godkänt av kunden men aldrig levererat. Kundens text finns i chatten nedan.'
              : 'Kundmejl via chatten – kunden har godkänt ett AI-utkastat mejl till kundtjänst. Svara via mail, inte i chatten.'}
          </div>
        ) : (
          conversation.isMailHandoff && (
            <div className="rounded-xl bg-orange-50 px-4 py-2.5 text-sm font-medium text-orange-800">
              Inkom utanför öppettid – hanteras via mail. Du behöver inte svara i chatten.
            </div>
          )
        )}

        {isMine && (
          <div className="rounded-xl bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700">
            Du hanterar detta ärende – skriv ditt svar i chatten.
          </div>
        )}

        {canTake && (
          <button
            type="button"
            disabled={takeHandoff.isPending}
            onClick={() => takeHandoff.mutate(conversation.id)}
            className="w-full rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {takeHandoff.isPending ? 'Tar över...' : 'Ta över chatt'}
          </button>
        )}

        {canAssign && roster.length > 0 && (
          <select
            defaultValue=""
            onChange={(event) => {
              const agentId = event.target.value;
              if (!agentId) return;
              assignHandoff.mutate({ conversationId: conversation.id, agentId });
              event.target.value = '';
            }}
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-700"
          >
            <option value="" disabled>
              Tilldela till…
            </option>
            {roster.map((agent) => {
              const free = Math.max(0, agent.load.max - agent.load.active);
              const full = free <= 0;
              return (
                <option key={agent.userId} value={agent.userId}>
                  {agent.alias} · {full ? 'fullt – tilldela ändå' : `${free} lediga`}
                </option>
              );
            })}
          </select>
        )}

        {canResolve && (
          <button
            type="button"
            disabled={resolveHandoff.isPending}
            onClick={() => resolveHandoff.mutate(conversation.id)}
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            {resolveHandoff.isPending ? 'Avslutar...' : 'Markera som löst'}
          </button>
        )}
      </div>
    </aside>
  );
}
